/**
 * The calendar tab's writes: the session being composed in the column, the recurring series being
 * composed in its dialog, and the moves made on the grid — with what each one does while the
 * server has not answered and after it has.
 *
 * Decisions are `model/`'s (`sessionDraft`, `recurringSeries`, `sessionMove`, `writeRefusal`); this
 * owns which of them is open, the requests, and what the screen says about them.
 *
 * ── Every write names the grid's zone ────────────────────────────────────────────────────────
 * The hour the trainer picked was read on the grid's clock, so that clock travels with it. There is
 * no retry without the zone: the server's own fallback is trainer → CLIENT → Warsaw, which is not
 * the clock the digits came off. A zone the server cannot resolve is a refusal the screen says.
 *
 * ── A new session ────────────────────────────────────────────────────────────────────────────
 * The draft is state HERE rather than in the column's form, because the grid draws it too — as a
 * block the trainer can drag and stretch — and both must show one draft. It is checked against its
 * own week (`bookingCalendar` over the week the draft stands in, the same cache entry the week view
 * reads). A refusal keeps the form open and red with the server's reason; success draws the new
 * block where it was composed (`createdNotYetRead`) until the calendar's next read has it, then
 * opens it in the column, briefly marked.
 *
 * ── A series ─────────────────────────────────────────────────────────────────────────────────
 * Its success and failure are handled on the MUTATION, not on one `mutate` call, so they run
 * whatever the window does meanwhile; and the window cannot be closed while it is sending, so no
 * second series can follow the first. The plan survives "Jednorazowa" and back.
 *
 * ── A move ───────────────────────────────────────────────────────────────────────────────────
 * The block is drawn where it was dropped (`withPendingMoves`) for as long as the server's reading
 * of it is still the one it was dropped from — through a failed refetch too. Each drop carries its
 * own token, so an earlier drop settling never clears a later one. A refusal takes it back and
 * says why where it was made: over the grid for a drop, in the form for "Przełóż".
 *
 * Every write invalidates the whole `['booking']` root — the grid, the hours, the decisions and
 * the free starts all read one calendar, and a created or moved session changes any of them.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  usePostCalendarEventIdReschedule,
  usePostCalendarevent,
  usePostCalendareventRecurring,
} from '~/shared/api/generated/endpoints/calendar-event/calendar-event';
import type { TrainWithMeModulesTrainingDtosResponsesRecurringSessionsResponse as RecurringSessionsResponse } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { type FailedWrite, readBookingAgain } from '~/features/credit-ledger';

import {
  trainerAvailabilityQueryOptions,
  trainerScheduleQueryOptions,
} from '../api/bookingQueries';
import { bookingCalendar, type BookingCalendar } from '../model/bookingCalendar';
import { readBookingSession, type BookingSession } from '../model/bookingSession';
import type { TimeSpan } from '../model/busyTime';
import { visibleRange } from '../model/calendarRange';
import { weekdayDateClock } from '../model/calendarWords';
import {
  conflictedMemberCount,
  seriesFromDraft,
  seriesRequest,
  type ClientSeries,
  type SeriesOccurrence,
  type SeriesPlan,
} from '../model/recurringSeries';
import {
  createRequest,
  draftAt,
  draftForDay,
  draftFromSpan,
  namesClient,
  nextStart,
  reviewDraft,
  withKind,
  withSpan,
  createdFromRequest,
  type CreatedSession,
  type DraftReview,
  type SessionDraft,
} from '../model/sessionDraft';
import { moveSubmission, type PendingMove } from '../model/sessionMove';
import { writeRefusal } from '../model/writeRefusal';
import { wallReading, type CivilDay } from '../model/trainerClock';
import type { BookingCalendarState } from './useBookingCalendar';

/** The composer's form, as the column draws it. */
export interface DraftState {
  readonly draft: SessionDraft;
  readonly review: DraftReview;
  readonly saving: boolean;
  /** The server's reason the last attempt was refused — the form's red state. */
  readonly refusal: string | null;
}

/** Where a move was made — and so where its refusal is said. */
export type MoveOrigin = 'grid' | 'form';

export interface SessionWrites {
  readonly composing: DraftState | null;
  /** The series window's plan while it is open. */
  readonly seriesSeed: SeriesPlan | null;
  readonly seriesSaving: boolean;
  /** Why the last attempt to create the series was refused; the window stays open. */
  readonly seriesRefusal: string | null;
  /** Sessions just written, marked on the grid for a moment. */
  readonly fresh: ReadonlySet<number>;
  readonly pendingMoves: ReadonlyMap<number, PendingMove>;
  /** Sessions created and not yet read back, drawn where they were composed. */
  readonly created: readonly CreatedSession[];
  /**
   * Why the last move went back, where it was made, and which session it was (`null` for a gesture
   * refused before it named one) — so a refusal is shown only beside the session it belongs to.
   */
  readonly moveRefusal: {
    readonly message: string;
    readonly origin: MoveOrigin;
    readonly sessionId: number | null;
  } | null;
  readonly announcement: string;
  readonly startNow: () => void;
  readonly startFromSpan: (span: TimeSpan) => void;
  /** Starts a session on a month day; a day already past starts nothing and says so over the grid. */
  readonly startOnDay: (day: CivilDay) => void;
  /** A session is being sent: the draft may not be closed, replaced or handed to a series. */
  readonly draftLocked: boolean;
  readonly editDraft: (patch: Partial<SessionDraft>) => void;
  readonly setDraftKind: (kind: SessionDraft['kind']) => void;
  readonly setDraftSpan: (span: TimeSpan) => void;
  readonly closeDraft: () => void;
  readonly submitDraft: () => void;
  readonly openSeries: () => void;
  /** The window handing back to the single session — "Jednorazowa". The plan is kept. */
  readonly backToOnce: (plan: SeriesPlan) => void;
  /** Close the window — refused while it is sending. The plan is kept for "Powtarzaj…". */
  readonly closeSeries: (plan: SeriesPlan) => void;
  readonly submitSeries: (plan: ClientSeries, occurrences: readonly SeriesOccurrence[]) => void;
  /** A drop, a resize or the "Przełóż" form. Resolves `true` once the server accepted it. */
  readonly reschedule: (
    session: BookingSession,
    target: TimeSpan,
    origin: MoveOrigin,
  ) => Promise<boolean>;
  readonly dismissMoveRefusal: () => void;
  readonly freshSeen: (id: number) => void;
}

function failureOf(error: unknown): FailedWrite | null {
  return error instanceof ApiError ? { status: error.status, code: error.code } : null;
}

export function useSessionWrites(state: BookingCalendarState): SessionWrites {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const announcer = useAnnouncer();
  const [draft, setDraft] = useState<SessionDraft | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [seriesSeed, setSeriesSeed] = useState<SeriesPlan | null>(null);
  // The series the trainer composed and handed back with "Jednorazowa" — reopened as they left it.
  const [keptSeries, setKeptSeries] = useState<SeriesPlan | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<number>>(new Set());
  const [pendingMoves, setPendingMoves] = useState<ReadonlyMap<number, PendingMove>>(new Map());
  const [created, setCreated] = useState<readonly CreatedSession[]>([]);
  const [moveRefusal, setMoveRefusal] = useState<SessionWrites['moveRefusal']>(null);
  const moveToken = useRef(0);

  // The draft is checked against its own week — the week view's own cache entry.
  const draftWeek = visibleRange('week', draft?.day ?? state.today);
  const weekEvents = useQuery({
    ...trainerScheduleQueryOptions(draftWeek),
    enabled: draft !== null,
  });
  const weekHours = useQuery({
    ...trainerAvailabilityQueryOptions(draftWeek),
    enabled: draft !== null,
  });
  const week: BookingCalendar | null =
    weekEvents.data === undefined || weekHours.data === undefined
      ? null
      : bookingCalendar({
          events: weekEvents.data,
          availability: weekHours.data,
          range: draftWeek,
          zone: state.zone,
          nowMs: state.nowMs,
        });

  const readAgain = () => readBookingAgain(queryClient);

  /**
   * Whether the read of the range on screen failed — only that one: a neighbour's failed prefetch
   * says nothing about the block the trainer is looking at.
   */
  const shownReadFailed = () =>
    queryClient.getQueryState(trainerScheduleQueryOptions(state.range).queryKey)?.status ===
    'error';

  const when = (startMs: number) => weekdayDateClock(startMs, state.zone, i18n.language);

  /** Read the calendar again, then mark what was written and open the first of it. */
  const showWritten = async (ids: readonly number[], day: CivilDay) => {
    await readAgain();
    setFresh(new Set(ids));
    const first = ids[0];
    if (first !== undefined) state.showSession(first, day);
  };

  // Answered on the MUTATION: the draft cannot change while it is out (`draftLocked`), and a late
  // answer lands on the session it was about, not on whatever the column shows by then.
  const create = usePostCalendarevent({
    mutation: {
      onSuccess: (session, { data }) => {
        if (data === undefined) return;
        const week = visibleRange('week', data.localDate ?? state.today);
        const readsBefore =
          queryClient.getQueryState(trainerScheduleQueryOptions(week).queryKey)?.dataUpdateCount ??
          0;
        const written = createdFromRequest(session.id, data, state.zone, readsBefore);
        announcer.announce(t('web:booking.create.created', { when: when(written.span.startMs) }));
        setCreated((current) => [...current, written]);
        setDraft(null);
        void showWritten([session.id], data.localDate ?? state.today);
      },
      onError: (error) => {
        // Said once, by the form's own alert beside the primary.
        const refused = writeRefusal(failureOf(error));
        setRefusal(t(refused.key));
        if (refused.refetch) void readAgain();
      },
    },
  });
  const move = usePostCalendarEventIdReschedule();
  const series = usePostCalendareventRecurring({
    mutation: {
      // On the mutation, so a result is never lost to whatever the window did meanwhile.
      onSuccess: (result: RecurringSessionsResponse) => {
        setSeriesSeed(null);
        setKeptSeries(null);
        setDraft(null);
        const members = result.members ?? [];
        const conflicted = conflictedMemberCount(result.conflicts);
        announcer.announce(
          conflicted === 0
            ? t('calendar.create.toast.created', { count: members.length })
            : t('web:booking.series.createdConflicts', { count: conflicted }),
        );
        const first = members[0];
        const firstDay = first === undefined ? null : readBookingSession(first, state.zone).day;
        void showWritten(
          members.map((member) => member.id),
          firstDay ?? state.selectedDay,
        );
      },
    },
  });

  const begin = (next: SessionDraft) => {
    if (create.isPending) return;
    state.closeSession();
    setRefusal(null);
    setKeptSeries(null);
    setDraft(next);
  };

  const review =
    draft === null
      ? null
      : reviewDraft(draft, {
          zone: state.zone,
          nowMs: state.nowMs,
          sessions: week?.sessions ?? null,
          working: week?.working.get(draft.day) ?? null,
          blocks: week?.blocks ?? [],
          call: state.callChoice,
          standingLink: state.standingCallLink,
        });

  const edited = (next: (current: SessionDraft) => SessionDraft) => {
    setRefusal(null);
    setDraft((current) => {
      if (current === null) return null;
      const changed = next(current);
      // A series kept for another client is not this draft's any more.
      if (changed.clientId !== current.clientId) setKeptSeries(null);
      return changed;
    });
  };

  /** A gesture that would start a session in the past: nothing opens, the grid says why. */
  const refusePast = () => {
    setMoveRefusal({
      message: t('web:booking.create.pastGesture'),
      origin: 'grid',
      sessionId: null,
    });
  };

  /** The server's reading of a session's span — what a drop is recorded as moving it FROM. */
  const serverSpan = (session: BookingSession): TimeSpan | null => {
    const read =
      state.content?.kind === 'ready'
        ? state.content.calendar.sessions.find((entry) => entry.id === session.id)
        : undefined;
    const source = read ?? session;
    return source.startMs === null || source.endMs === null
      ? null
      : { startMs: source.startMs, endMs: source.endMs };
  };

  return {
    composing:
      draft === null || review === null
        ? null
        : { draft, review, saving: create.isPending, refusal },
    seriesSeed,
    seriesSaving: series.isPending,
    seriesRefusal: series.isError ? t(writeRefusal(failureOf(series.error)).key) : null,
    fresh,
    pendingMoves,
    created,
    moveRefusal,
    announcement: announcer.message,

    startNow: () => {
      begin(draftAt(nextStart(state.nowMs, state.zone), state.policy));
    },
    startFromSpan: (span) => {
      const next = draftFromSpan(span, state.zone, state.policy, state.nowMs);
      if (next === null) refusePast();
      else begin(next);
    },
    startOnDay: (day) => {
      const working =
        state.content?.kind === 'ready' ? state.content.calendar.working.get(day) : undefined;
      const next = draftForDay(day, {
        nowMs: state.nowMs,
        zone: state.zone,
        working: working ?? null,
        policy: state.policy,
      });
      if (next === null) refusePast();
      else begin(next);
    },
    draftLocked: create.isPending,
    editDraft: (patch) => {
      edited((current) => ({ ...current, ...patch }));
    },
    setDraftKind: (kind) => {
      edited((current) => withKind(current, kind, state.policy));
    },
    setDraftSpan: (span) => {
      edited((current) => withSpan(current, span, state.zone));
    },
    closeDraft: () => {
      if (create.isPending) return;
      setDraft(null);
      setRefusal(null);
      setKeptSeries(null);
    },
    submitDraft: () => {
      if (draft === null || review === null || review.problem !== null || create.isPending) return;
      if (!namesClient(draft)) return;
      setRefusal(null);
      announcer.announce(t('web:booking.create.saving'));
      create.mutate({ data: createRequest(draft, state.zone, review.callLink) });
    },
    openSeries: () => {
      if (draft === null || create.isPending) return;
      if (series.isError) series.reset();
      setSeriesSeed(
        keptSeries === null
          ? seriesFromDraft(draft)
          : {
              ...keptSeries,
              clientId: draft.clientId,
              kind: draft.kind,
              durationMinutes: draft.durationMinutes,
            },
      );
    },
    backToOnce: (plan) => {
      if (series.isPending) return;
      setKeptSeries(plan);
      setSeriesSeed(null);
      edited((current) => ({
        ...current,
        clientId: plan.clientId,
        kind: plan.kind,
        durationMinutes: plan.durationMinutes,
      }));
    },
    closeSeries: (plan) => {
      // Never while sending: the result would have nowhere to land, and a second series could go.
      if (series.isPending) return;
      if (series.isError) series.reset();
      // Kept, so "Powtarzaj…" reopens the days, weeks and moves the trainer left.
      setKeptSeries(plan);
      setSeriesSeed(null);
    },
    submitSeries: (plan, occurrences) => {
      if (series.isPending) return;
      announcer.announce(t('web:booking.series.saving'));
      series.mutate({ data: seriesRequest(plan, occurrences, state.zone) });
    },
    reschedule: async (session, target, origin) => {
      const refuse = (message: string) => {
        // Said once, by the alert where the move was made.
        setMoveRefusal({ message, origin, sessionId: session.id });
        return false;
      };
      if (move.isPending) return refuse(t('web:booking.move.stillSaving'));
      const submission = moveSubmission(session, target, state.zone, state.nowMs);
      if (!submission.ok) {
        return refuse(
          t(
            submission.problem === 'midnight'
              ? 'web:booking.refusal.midnight_is_not_an_hour'
              : submission.problem === 'past'
                ? 'web:booking.move.past'
                : 'web:booking.create.problem.duration',
          ),
        );
      }
      const from = serverSpan(session);
      moveToken.current += 1;
      const token = moveToken.current;
      setMoveRefusal(null);
      if (from !== null) {
        setPendingMoves((current) => new Map(current).set(session.id, { from, to: target, token }));
      }
      const settle = () => {
        setPendingMoves((current) => {
          if (current.get(session.id)?.token !== token) return current;
          const next = new Map(current);
          next.delete(session.id);
          return next;
        });
      };
      announcer.announce(t('web:booking.move.saving'));
      try {
        await move.mutateAsync({ id: session.id, data: submission.body });
      } catch (error) {
        settle();
        const refused = writeRefusal(failureOf(error));
        if (refused.refetch) void readAgain();
        return refuse(t(refused.key));
      }
      announcer.announce(t('web:booking.move.moved', { when: when(target.startMs) }));
      // "Przełóż" to another week: the grid goes there, and the column keeps showing the session.
      if (origin === 'form') {
        state.showSession(session.id, wallReading(target.startMs, state.zone).day);
      }
      await readAgain();
      // A failed read keeps the block where it was dropped; the stale line says the rest.
      if (!shownReadFailed()) settle();
      setFresh(new Set([session.id]));
      return true;
    },
    dismissMoveRefusal: () => {
      setMoveRefusal(null);
    },
    freshSeen: (id) => {
      setFresh((current) => {
        if (!current.has(id)) return current;
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    },
  };
}
